// Telefon klientky: podpis smlouvy prstem (/p/<p>, odkaz z SMS, 3. 11. 2026, schválený mockup 2/2).
// Bez přihlášení, autorizuje token z SMS. Server: /api/smlouva-podpis-verejne.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { CircleCheck, FileText, ShieldCheck, Loader2, AlertCircle, ChevronUp } from "lucide-react";
import { Pil } from "../components/sales/salesUi.jsx";
import { upravMd } from "../lib/smlouvaMd.js";

const API = "/api/smlouva-podpis-verejne";
async function post(data) {
  const r = await fetch(API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || "Něco se nepovedlo, zkuste to prosím znovu.");
  return j;
}
const MIN_DELKA = 120; // px tahu, ať podpis není jen tečka

// Plátno na podpis prstem. 3. 11. 2026: na iPhonu (Safari) se přes React pointer události nekreslilo,
// proto vlastní posluchače přímo na plátně (pointer, a když prohlížeč pointer neumí, dotyk), bez
// pasivního režimu a bez „pointerleave“, který Safari při zachycení prstu posílá hned po dotyku.
function Platno({ onZmena }) {
  const ref = useRef(null);
  const zmena = useRef(onZmena);
  zmena.current = onZmena;
  const delka = useRef(0);

  useEffect(() => {
    const c = ref.current;
    let sirka = 0;
    let kresli = false;
    let posledni = null;
    const ctx = () => c.getContext("2d");
    const styl = () => { const x = ctx(); x.lineWidth = 2.6; x.lineCap = "round"; x.lineJoin = "round"; x.strokeStyle = "#241430"; };
    // Na mobilu chodí resize i při schování lišty prohlížeče při posunu. Podpis se nesmí smazat:
    // při stejné šířce se nic nedělá, při jiné (otočení telefonu) se dosavadní podpis přenese.
    const nastav = () => {
      const r = c.getBoundingClientRect();
      if (!r.width || Math.round(r.width) === sirka) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      let puvodni = null;
      if (sirka && delka.current > 0) { puvodni = document.createElement("canvas"); puvodni.width = c.width; puvodni.height = c.height; puvodni.getContext("2d").drawImage(c, 0, 0); }
      sirka = Math.round(r.width);
      c.width = Math.round(r.width * dpr); c.height = Math.round(r.height * dpr);
      const x = ctx();
      if (puvodni) x.drawImage(puvodni, 0, 0, c.width, c.height);
      x.setTransform(dpr, 0, 0, dpr, 0, 0);
      styl();
      if (puvodni && delka.current >= MIN_DELKA) zmena.current(c.toDataURL("image/png"));
    };
    const bod = (x, y) => { const r = c.getBoundingClientRect(); return { x: x - r.left, y: y - r.top }; };
    const zacni = (x, y) => { nastav(); kresli = true; posledni = bod(x, y); const k = ctx(); k.beginPath(); k.arc(posledni.x, posledni.y, 1.2, 0, Math.PI * 2); k.fillStyle = "#241430"; k.fill(); };
    const tahni = (x, y) => {
      if (!kresli) return;
      const p = bod(x, y); const q = posledni; const k = ctx();
      k.beginPath(); k.moveTo(q.x, q.y); k.lineTo(p.x, p.y); k.stroke();
      delka.current += Math.hypot(p.x - q.x, p.y - q.y);
      posledni = p;
    };
    const skonci = () => {
      if (!kresli) return;
      kresli = false;
      zmena.current(delka.current >= MIN_DELKA ? c.toDataURL("image/png") : null);
    };
    const opt = { passive: false };
    const zrus = [];
    const pridej = (cil, typ, fn) => { cil.addEventListener(typ, fn, opt); zrus.push(() => cil.removeEventListener(typ, fn, opt)); };
    if (window.PointerEvent) {
      pridej(c, "pointerdown", (e) => { e.preventDefault(); try { c.setPointerCapture(e.pointerId); } catch { /* nevadí */ } zacni(e.clientX, e.clientY); });
      pridej(c, "pointermove", (e) => { if (!kresli) return; e.preventDefault(); tahni(e.clientX, e.clientY); });
      pridej(c, "pointerup", () => skonci());
      pridej(c, "pointercancel", () => skonci());
      // Safari na iOS posouvá stránku i přes touch-action, když se dotyková událost nezastaví.
      pridej(c, "touchmove", (e) => e.preventDefault());
    } else {
      pridej(c, "touchstart", (e) => { e.preventDefault(); const t = e.touches[0]; if (t) zacni(t.clientX, t.clientY); });
      pridej(c, "touchmove", (e) => { e.preventDefault(); const t = e.touches[0]; if (t) tahni(t.clientX, t.clientY); });
      pridej(c, "touchend", () => skonci());
      pridej(c, "touchcancel", () => skonci());
      pridej(c, "mousedown", (e) => zacni(e.clientX, e.clientY));
      pridej(window, "mousemove", (e) => tahni(e.clientX, e.clientY));
      pridej(window, "mouseup", () => skonci());
    }
    nastav();
    window.addEventListener("resize", nastav);
    return () => { zrus.forEach((f) => f()); window.removeEventListener("resize", nastav); };
  }, []);

  const smazat = () => {
    const c = ref.current; const x = c.getContext("2d");
    x.save(); x.setTransform(1, 0, 0, 1, 0, 0); x.clearRect(0, 0, c.width, c.height); x.restore();
    delka.current = 0; onZmena(null);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="text-[15px] font-bold text-slate-900">Váš podpis prstem</div>
        <button type="button" className="rounded-lg px-2 py-1 text-[14px] font-bold text-brand-deep" onClick={smazat}>Smazat</button>
      </div>
      <canvas ref={ref} className="mt-2 h-36 w-full touch-none select-none rounded-xl border-2 border-dashed border-brand-200 bg-white"
        style={{ WebkitUserSelect: "none", WebkitTouchCallout: "none" }} />
    </div>
  );
}

export default function PodpisTelefon() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [chyba, setChyba] = useState("");
  const [souhlas, setSouhlas] = useState(false);
  const [zahajeni, setZahajeni] = useState(false); // podpis na dálku: žádost o zahájení služeb
  const [podpis, setPodpis] = useState(null);
  const [zneni, setZneni] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { post({ akce: "telefon", p: token }).then(setData).catch((e) => setChyba(e.message)); }, [token]);
  const md = useMemo(() => upravMd(data?.smlouva?.obsah_md || ""), [data?.smlouva?.obsah_md]);

  const podepsat = async () => {
    if (!souhlas || !podpis || (data?.dalku && !zahajeni)) return;
    setBusy(true); setChyba("");
    try { setData(await post({ akce: "podepsat", p: token, souhlas: true, zahajeni: !!data?.dalku && zahajeni, podpis })); window.scrollTo(0, 0); }
    catch (e) { setChyba(e.message); } finally { setBusy(false); }
  };

  if (!data && chyba) return (
    <div className="flex min-h-screen flex-col items-center bg-slate-50 px-6 pt-16 text-center">
      <img src="/logo.svg" alt="FitWay40" className="mb-10 h-8" />
      <AlertCircle className="h-12 w-12 text-rose-500" />
      <div className="mt-4 font-serif text-2xl font-bold text-slate-900">Odkaz neplatí</div>
      <p className="mt-2 max-w-md text-[16px] text-slate-600">{chyba}</p>
    </div>
  );
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Načítám smlouvu…</div>;

  if (data.stav === "podepsano") return (
    <div className="flex min-h-screen flex-col items-center bg-slate-50 px-6 pt-16 text-center">
      <img src="/logo.svg" alt="FitWay40" className="mb-10 h-8" />
      <span className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><CircleCheck className="h-11 w-11" /></span>
      <div className="mt-5 font-serif text-3xl font-bold text-slate-900">Hotovo, děkujeme!</div>
      <p className="mt-3 max-w-md text-[16px] leading-relaxed text-slate-600">Smlouva je podepsaná. <strong>Kopii vám posíláme e‑mailem</strong>{data.email ? <> na {data.email}</> : null}. Moc se těšíme na první trénink.</p>
      {data.podepsano && <div className="mt-6"><Pil ton="ok" casti={["Podepsáno", data.podepsano]} /></div>}
    </div>
  );

  const s = data.smlouva || {};
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 px-4 pb-5 pt-4 text-white sm:px-8">
        <div className="mx-auto max-w-xl">
          <img src="/logo-bila.svg" alt="FitWay40" className="h-7" />
          <div className="mt-4 font-serif text-2xl font-bold leading-tight">Podpis smlouvy</div>
          <div className="mt-1 text-[15px] text-white/80">Dobrý den{data.osloveni ? `, ${data.osloveni}` : ""}, moc nás těší, že začínáte s námi.</div>
        </div>
      </header>
      <main className="mx-auto max-w-xl space-y-4 px-4 py-5 sm:px-0">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-[13px] font-bold uppercase tracking-wide text-slate-500">Podepisujete</div>
          <div className="mt-1 text-[17px] font-bold text-slate-900">{s.nazev}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Pil ton="znacka" casti={[data.muz ? "Klient" : "Klientka", s.jmeno]} />
            {!data.dalku && <Pil ton="neutralni" casti={["s", "LifeEducation s.r.o. (FitWay40)"]} />}
            {s.balicek && <Pil ton="znacka" casti={["Balíček", s.balicek]} />}
            <Pil ton="neutralni" casti={["Datum", s.datum]} />
            {data.dalku && data.plati_do && <Pil ton="pozor" casti={["Odkaz platí do", data.plati_do]} />}
          </div>
          <button type="button" className="btn-secondary mt-3 w-full justify-center" onClick={() => setZneni((x) => !x)}>
            {zneni ? <ChevronUp className="h-4 w-4" /> : <FileText className="h-4 w-4" />}{zneni ? "Skrýt znění" : "Zobrazit celé znění"}
          </button>
          {zneni && <article className="prose prose-slate prose-sm mt-3 max-w-none border-t border-slate-100 pt-3 prose-h1:text-lg prose-h2:text-base"><ReactMarkdown>{md}</ReactMarkdown></article>}
        </div>
        {data.dalku && (
          <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4 text-[15px] leading-relaxed text-sky-950">
            <div className="mb-1.5 flex items-center gap-2 font-bold"><ShieldCheck className="h-5 w-5 text-sky-700" />Poučení o odstoupení od smlouvy</div>
            Smlouvu uzavíráte na dálku, proto máte <strong>právo od ní odstoupit do 14 dnů</strong> od podpisu, a to bez udání důvodu. Stačí nám napsat na <strong>klienti@zateckal.cz</strong>. Pokud služby začnou dřív (například první trénink nebo jídelníček), při odstoupení uhradíte jen <strong>poměrnou část</strong> za to, co už proběhlo.
          </div>
        )}
        <button type="button" role="checkbox" aria-checked={souhlas} onClick={() => setSouhlas((x) => !x)}
          className={`flex w-full items-start gap-3 rounded-2xl border-2 p-4 text-left ${souhlas ? "border-brand bg-brand-50" : "border-slate-300 bg-white"}`}>
          <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${souhlas ? "bg-brand text-white" : "border-2 border-slate-300 bg-white"}`}>{souhlas && <CircleCheck className="h-4 w-4" />}</span>
          <span className="text-[15px] leading-snug text-brand-950">{(() => { const t = data.souhlas_text || ""; const i = t.indexOf(". "); return i > 0 ? <><strong>{t.slice(0, i + 1)}</strong>{t.slice(i + 1)}</> : t; })()}</span>
        </button>
        {data.dalku && (
          <button type="button" role="checkbox" aria-checked={zahajeni} onClick={() => setZahajeni((x) => !x)}
            className={`flex w-full items-start gap-3 rounded-2xl border-2 p-4 text-left ${zahajeni ? "border-brand bg-brand-50" : "border-slate-300 bg-white"}`}>
            <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${zahajeni ? "bg-brand text-white" : "border-2 border-slate-300 bg-white"}`}>{zahajeni && <CircleCheck className="h-4 w-4" />}</span>
            <span className="text-[15px] leading-snug text-brand-950"><strong>Žádám o zahájení služeb hned,</strong> ještě před uplynutím 14 dnů. Beru na vědomí, že při odstoupení uhradím poměrnou část za služby, které už proběhly.</span>
          </button>
        )}
        <Platno onZmena={setPodpis} />
        <div className="-mt-2 text-center text-[13px] text-slate-500">{s.jmeno}</div>
        {chyba && <div className="flex items-start gap-2 rounded-xl bg-rose-50 px-3 py-2.5 text-sm font-semibold text-rose-700"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{chyba}</div>}
        <button type="button" className="btn-primary min-h-[56px] w-full justify-center text-[17px] !bg-emerald-600 hover:!bg-emerald-700 disabled:opacity-50" disabled={!souhlas || !podpis || (data.dalku && !zahajeni) || busy} onClick={podepsat}>
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CircleCheck className="h-5 w-5" />}Podepsat smlouvu
        </button>
        {(!souhlas || !podpis || (data.dalku && !zahajeni)) && <p className="text-center text-[13px] text-slate-500">{!souhlas ? "Nejdřív zaškrtněte souhlas se smlouvou." : data.dalku && !zahajeni ? "Zaškrtněte i žádost o zahájení služeb." : "Podepište se prstem do rámečku."}</p>}
        <p className="flex items-start gap-2 px-1 text-[13px] text-slate-500"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{data.prisel === "email" ? "Odkaz přišel na váš e‑mail" : `Odkaz přišel SMS na vaše číslo ${s.telefon}`} a platí jen pro tuto smlouvu. Podepsanou smlouvu{data.dalku ? " i s poučením" : ""} vám pošleme e‑mailem.</p>
      </main>
    </div>
  );
}
